import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Building2, ShieldCheck, Bell, CreditCard, FileText, Scale } from "lucide-react";
import { BUSINESS_TYPE_LABELS } from "@/lib/constants";

export default function Settings() {
  const { business } = useAuth();
  const [subs, setSubs] = useState([]);
  const [prefs, setPrefs] = useState({ orders: true, partners: true, subscription: true });

  useEffect(() => { api.get("/subscription/mine").then((r) => setSubs(r.data.subscriptions)).catch(() => {}); }, []);
  const active = subs.find((s) => s.payment_status === "paid");

  return (
    <div className="space-y-6" data-testid="settings-page">
      <h1 className="font-heading text-2xl font-extrabold text-stone-900 sm:text-3xl">Settings</h1>

      <Accordion type="multiple" defaultValue={["business", "billing"]} className="space-y-3">
        <SettingBlock value="business" icon={Building2} title="Business Information">
          <div className="space-y-2 text-sm">
            <Line label="Business Name" value={business?.name} />
            <Line label="Business Type" value={BUSINESS_TYPE_LABELS[business?.business_type] || business?.business_type} />
            <Line label="Profile Code" value={business?.profile_code} mono />
            <Line label="Address" value={business?.address} />
          </div>
        </SettingBlock>

        <SettingBlock value="security" icon={ShieldCheck} title="Login & Security">
          <div className="space-y-2 text-sm text-stone-600">
            <Line label="Login Method" value="Mobile OTP (verified)" />
            <Line label="Verified Mobile" value={business?.owner_mobile} />
            <p className="pt-2 text-xs text-stone-400">Owners and operators sign in using a one-time password sent to the registered mobile number.</p>
          </div>
        </SettingBlock>

        <SettingBlock value="notifications" icon={Bell} title="Notification Preferences">
          <div className="space-y-3">
            {[["orders", "Order updates"], ["partners", "Partner activity"], ["subscription", "Subscription & billing"]].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between">
                <Label htmlFor={`pref-${k}`} className="text-sm text-stone-700">{l}</Label>
                <Switch id={`pref-${k}`} data-testid={`pref-${k}`} checked={prefs[k]} onCheckedChange={(v) => setPrefs({ ...prefs, [k]: v })} />
              </div>
            ))}
          </div>
        </SettingBlock>

        <SettingBlock value="billing" icon={CreditCard} title="Subscription & Billing">
          {active ? (
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between"><span className="text-stone-500">Current Plan</span><span className="font-semibold">{active.plan_name}</span></div>
              <Line label="Amount" value={`₹${active.amount} (${active.billing_cycle})`} />
              <Line label="Payment Status" value={<StatusBadge status={active.payment_status} />} raw />
              <Line label="Started" value={active.start_date ? new Date(active.start_date).toLocaleDateString() : "—"} />
              <Line label="Renews / Expires" value={active.expiry_date ? new Date(active.expiry_date).toLocaleDateString() : "—"} />
              <Line label="Transaction Ref" value={active.transaction_ref} mono />
            </div>
          ) : <p className="text-sm text-stone-400">No active subscription.</p>}
        </SettingBlock>

        <SettingBlock value="privacy" icon={FileText} title="Privacy Policy">
          <p className="text-sm text-stone-600">RESTRO-VENDS stores your business and license information securely. Uploaded documents are access-controlled and served only to authorized users. We do not share your data with third parties without consent.</p>
          <a href="/privacy" target="_blank" rel="noreferrer" className="mt-2 inline-block text-sm font-semibold text-[#0D5C3A] hover:underline" data-testid="settings-privacy-link">Read the full Privacy Policy →</a>
        </SettingBlock>

        <SettingBlock value="terms" icon={Scale} title="Terms & Conditions">
          <p className="text-sm text-stone-600">By using RESTRO-VENDS you agree to maintain accurate business information, keep valid licenses, and use the platform for legitimate supply-chain activities. Accounts may be suspended for policy violations.</p>
          <a href="/terms" target="_blank" rel="noreferrer" className="mt-2 inline-block text-sm font-semibold text-[#0D5C3A] hover:underline" data-testid="settings-terms-link">Read the full Terms & Conditions →</a>
        </SettingBlock>
      </Accordion>
    </div>
  );
}

function SettingBlock({ value, icon: Icon, title, children }) {
  return (
    <AccordionItem value={value} className="overflow-hidden rounded-2xl border border-[#E5E0D8] bg-white px-5" data-testid={`setting-${value}`}>
      <AccordionTrigger className="hover:no-underline">
        <span className="flex items-center gap-3 font-heading font-semibold text-stone-800"><Icon className="h-5 w-5 text-[#0D5C3A]" /> {title}</span>
      </AccordionTrigger>
      <AccordionContent className="pb-5">{children}</AccordionContent>
    </AccordionItem>
  );
}

function Line({ label, value, mono, cap, raw }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-stone-500">{label}</span>
      {raw ? value : <span className={`text-right font-medium text-stone-800 ${mono ? "font-mono" : ""} ${cap ? "capitalize" : ""}`}>{value || "—"}</span>}
    </div>
  );
}
